import axios from 'axios';

const BASE = '/api/admin/cms/pages';

/**
 * List all pages, drafts included.
 *
 * @param {Object} filters - Optional `locale`, `status` and `search` filters
 * @returns {Promise<Array>}
 */
export function getPages(filters = {}) {
  return axios.get(BASE, { params: filters }).then(res => res.data);
}

/**
 * Get a single page including its body.
 *
 * @param {String} id - Page id
 * @returns {Promise<Object>}
 */
export function getPage(id) {
  return axios.get(`${BASE}/${id}`).then(res => res.data);
}

/**
 * Create a page.
 *
 * @param {Object} payload - Page fields
 * @returns {Promise<Object>}
 */
export function createPage(payload) {
  return axios.post(BASE, payload).then(res => res.data);
}

/**
 * Update a page.
 *
 * @param {String} id - Page id
 * @param {Object} payload - Fields to change
 * @returns {Promise<Object>}
 */
export function updatePage(id, payload) {
  return axios.put(`${BASE}/${id}`, payload).then(res => res.data);
}

/**
 * Delete a page.
 *
 * @param {String} id - Page id
 * @returns {Promise<Object>}
 */
export function deletePage(id) {
  return axios.delete(`${BASE}/${id}`).then(res => res.data);
}

/**
 * List revisions for a page, newest first.
 *
 * @param {String} id - Page id
 * @returns {Promise<Array>}
 */
export function getRevisions(id) {
  return axios.get(`${BASE}/${id}/revisions`).then(res => res.data);
}

/**
 * Restore a page to an earlier revision.
 *
 * @param {String} id - Page id
 * @param {String} revisionId - Revision to restore
 * @returns {Promise<Object>}
 */
export function restoreRevision(id, revisionId) {
  return axios
    .post(`${BASE}/${id}/restore-revision`, { revisionId })
    .then(res => res.data);
}

const ANNOUNCEMENTS = '/api/admin/cms/announcements';

/**
 * List all announcements, newest first.
 *
 * @returns {Promise<Array>}
 */
export function getAnnouncements() {
  return axios.get(ANNOUNCEMENTS).then(res => res.data);
}

/**
 * Create an announcement.
 *
 * @param {Object} payload - Announcement fields
 * @returns {Promise<Object>}
 */
export function createAnnouncement(payload) {
  return axios.post(ANNOUNCEMENTS, payload).then(res => res.data);
}

/**
 * Update an announcement.
 *
 * @param {String} id - Announcement id
 * @param {Object} payload - Fields to change
 * @returns {Promise<Object>}
 */
export function updateAnnouncement(id, payload) {
  return axios.put(`${ANNOUNCEMENTS}/${id}`, payload).then(res => res.data);
}

/**
 * Delete an announcement.
 *
 * @param {String} id - Announcement id
 * @returns {Promise<Object>}
 */
export function deleteAnnouncement(id) {
  return axios.delete(`${ANNOUNCEMENTS}/${id}`).then(res => res.data);
}

/**
 * List trashed pages.
 *
 * @returns {Promise<Array>}
 */
export function getTrash() {
  return axios.get(`${BASE}/trash`).then(res => res.data);
}

/**
 * Restore a trashed page.
 *
 * @param {String} id - Page id
 * @returns {Promise<Object>}
 */
export function restorePage(id) {
  return axios.post(`${BASE}/${id}/restore`).then(res => res.data);
}

/**
 * Permanently delete a trashed page.
 *
 * @param {String} id - Page id
 * @returns {Promise<Object>}
 */
export function purgePage(id) {
  return axios
    .delete(`${BASE}/${id}`, { params: { permanent: 'true' } })
    .then(res => res.data);
}

const SETTINGS = '/api/admin/cms/settings';

/**
 * Get the CMS settings singleton.
 *
 * @returns {Promise<Object>}
 */
export function getSettings() {
  return axios.get(SETTINGS).then(res => res.data);
}

/**
 * Update the CMS settings singleton.
 *
 * @param {Object} payload - siteName, tagline and/or theme
 * @returns {Promise<Object>}
 */
export function updateSettings(payload) {
  return axios.put(SETTINGS, payload).then(res => res.data);
}

const FOOTER = '/api/admin/cms/footer-menu';

/**
 * List all footer sections.
 *
 * @returns {Promise<Array>}
 */
export function getFooterSections() {
  return axios.get(FOOTER).then(res => res.data);
}

/**
 * Create a footer section.
 *
 * @param {Object} payload - `{ name, items?, order?, enabled? }`
 * @returns {Promise<Object>}
 */
export function createFooterSection(payload) {
  return axios.post(FOOTER, payload).then(res => res.data);
}

/**
 * Update a footer section, replacing its links.
 *
 * @param {String} id - Section id
 * @param {Object} payload - Fields to change, including the full `items` list
 * @returns {Promise<Object>}
 */
export function updateFooterSection(id, payload) {
  return axios.put(`${FOOTER}/${id}`, payload).then(res => res.data);
}

/**
 * Delete a footer section.
 *
 * @param {String} id - Section id
 * @returns {Promise<Object>}
 */
export function deleteFooterSection(id) {
  return axios.delete(`${FOOTER}/${id}`).then(res => res.data);
}

const POSTS = '/api/admin/cms/posts';

/**
 * List all blog posts, drafts included.
 *
 * Posts share the page collection: everything addressed by id (`getPage`,
 * `updatePage`, `deletePage`, revisions, trash) works on posts unchanged.
 * Only listing and creation are post-specific.
 *
 * @param {Object} filters - Optional `locale`, `status` and `search` filters
 * @returns {Promise<Array>}
 */
export function getPosts(filters = {}) {
  return axios.get(POSTS, { params: filters }).then(res => res.data);
}

/**
 * Create a blog post.
 *
 * @param {Object} payload - Post fields
 * @returns {Promise<Object>}
 */
export function createPost(payload) {
  return axios.post(POSTS, payload).then(res => res.data);
}

/**
 * Upload an image for use as a cover or inside a body.
 *
 * The server re-encodes it as a bounded JPEG and returns a site-relative
 * URL (`/uploads-cms/…`).
 *
 * @param {File} file - Image file from an `<input type="file">`
 * @returns {Promise<{ url: String }>}
 */
export function uploadImage(file) {
  const data = new FormData();
  data.append('image', file);

  return axios
    .post('/api/admin/cms/uploads', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then(res => res.data);
}

const BLOG_CATEGORIES = '/api/admin/cms/blog-categories';

/**
 * List blog categories, each with a `postCount`.
 *
 * @param {Object} filters - Optional `locale`
 * @returns {Promise<Array>}
 */
export function getBlogCategories(filters = {}) {
  return axios.get(BLOG_CATEGORIES, { params: filters }).then(res => res.data);
}

/**
 * Create a blog category.
 *
 * @param {Object} payload - `{ name, slug?, locale?, description?, order?, enabled? }`
 * @returns {Promise<Object>}
 */
export function createBlogCategory(payload) {
  return axios.post(BLOG_CATEGORIES, payload).then(res => res.data);
}

/**
 * Update a blog category.
 *
 * @param {String} id - Category id
 * @param {Object} payload - Fields to change
 * @returns {Promise<Object>}
 */
export function updateBlogCategory(id, payload) {
  return axios.put(`${BLOG_CATEGORIES}/${id}`, payload).then(res => res.data);
}

/**
 * Delete a blog category. Refused (409) while posts still use it.
 *
 * @param {String} id - Category id
 * @returns {Promise<Object>}
 */
export function deleteBlogCategory(id) {
  return axios.delete(`${BLOG_CATEGORIES}/${id}`).then(res => res.data);
}

const FAQ = '/api/admin/cms/faq';

/**
 * List FAQ categories, each with an `entryCount`.
 *
 * @param {Object} filters - Optional `locale`
 * @returns {Promise<Array>}
 */
export function getFaqCategories(filters = {}) {
  return axios
    .get(`${FAQ}/categories`, { params: filters })
    .then(res => res.data);
}

/**
 * Create a FAQ category.
 *
 * @param {Object} payload - `{ name, slug?, locale?, description?, order?, enabled? }`
 * @returns {Promise<Object>}
 */
export function createFaqCategory(payload) {
  return axios.post(`${FAQ}/categories`, payload).then(res => res.data);
}

/**
 * Update a FAQ category.
 *
 * @param {String} id - Category id
 * @param {Object} payload - Fields to change
 * @returns {Promise<Object>}
 */
export function updateFaqCategory(id, payload) {
  return axios.put(`${FAQ}/categories/${id}`, payload).then(res => res.data);
}

/**
 * Delete a FAQ category. Refused (409) while it still holds questions.
 *
 * @param {String} id - Category id
 * @returns {Promise<Object>}
 */
export function deleteFaqCategory(id) {
  return axios.delete(`${FAQ}/categories/${id}`).then(res => res.data);
}

/**
 * List the questions of a category, in display order, answers included.
 *
 * @param {String} categoryId - Category id
 * @returns {Promise<Array>}
 */
export function getFaqEntries(categoryId) {
  return axios
    .get(`${FAQ}/entries`, { params: { category: categoryId } })
    .then(res => res.data);
}

/**
 * Create a question.
 *
 * @param {Object} payload - `{ category, question, answer, anchor?, status?, featured?, keywords?, order? }`
 * @returns {Promise<Object>}
 */
export function createFaqEntry(payload) {
  return axios.post(`${FAQ}/entries`, payload).then(res => res.data);
}

/**
 * Update a question.
 *
 * @param {String} id - Entry id
 * @param {Object} payload - Fields to change
 * @returns {Promise<Object>}
 */
export function updateFaqEntry(id, payload) {
  return axios.put(`${FAQ}/entries/${id}`, payload).then(res => res.data);
}

/**
 * Delete a question.
 *
 * @param {String} id - Entry id
 * @returns {Promise<Object>}
 */
export function deleteFaqEntry(id) {
  return axios.delete(`${FAQ}/entries/${id}`).then(res => res.data);
}
